import type { LocaleMessages, Messages } from '../plugins/i18n'

/**
 * Deep merges multiple locale message objects
 * @param target - The target object to merge into
 * @param source - The source object to merge from
 * @returns The merged object
 */
function deepMerge(target: LocaleMessages, source: LocaleMessages): LocaleMessages {
  const result = { ...target }

  for (const key in source) {
    if (typeof source[key] === 'object' && source[key] !== null && !Array.isArray(source[key])) {
      result[key] = deepMerge(
        (result[key] as LocaleMessages) || {},
        source[key] as LocaleMessages
      )
    } else {
      result[key] = source[key]
    }
  }

  return result
}

/**
 * Merges locale files from multiple apps into a single messages object
 * @param localeModules - Array of locale modules from different apps
 * @returns Merged messages object
 */
export function mergeLocales(localeModules: Array<{ [locale: string]: LocaleMessages }>): Messages {
  const messages: Messages = {}

  // Iterate through each module
  for (const module of localeModules) {
    // Iterate through each locale in the module
    for (const locale in module) {
      if (!messages[locale]) {
        messages[locale] = {}
      }
      messages[locale] = deepMerge(messages[locale], module[locale])
    }
  }

  return messages
}

/**
 * Loads all locale files dynamically using glob imports
 * @returns Promise resolving to merged messages
 */
export async function loadAllLocales(): Promise<Messages> {
  const localeModules: Array<{ [locale: string]: LocaleMessages }> = []

  // Import all locale files from apps and core
  const appModules = import.meta.glob('/src/apps/*/locales/*.js', { eager: true })
  const coreModules = import.meta.glob('/src/core/locales/*.js', { eager: true })

  // Process app modules
  for (const path in appModules) {
    const module = appModules[path] as { default: LocaleMessages }

    // Extract app name and locale from path
    const matches = path.match(/\/src\/apps\/([^/]+)\/locales\/([^.]+)\.js/)
    if (matches) {
      const [, appName, locale] = matches

      // Create a module with namespaced messages
      const namespacedModule = {
        [locale]: {
          [appName]: module.default
        }
      }

      localeModules.push(namespacedModule)
    }
  }

  // Process core modules
  for (const path in coreModules) {
    const module = coreModules[path] as { default: LocaleMessages }

    // Extract locale from path
    const matches = path.match(/\/src\/core\/locales\/([^.]+)\.js/)
    if (matches) {
      const [, locale] = matches

      // Create a module with core namespace
      const namespacedModule = {
        [locale]: {
          core: module.default
        }
      }

      localeModules.push(namespacedModule)
    }
  }

  return mergeLocales(localeModules)
}
